/**
 * PRD.292 — Contextual prescription reference: a LEFT-docked, NON-MODAL side
 * panel (its own body-level portal, so it sits to the left of the New Pharmacy
 * Sale drawer rather than inside it). It shows the rendered prescription IMAGE
 * (reusing the shared `usePrescriptionImage` pipeline — the same document the
 * read-only drawer shows and downloads) as a lower-emphasis reference while the
 * pharmacist bills on the right.
 *
 * Non-modal: no backdrop, so Billing on the right stays interactive and keeps
 * focus. Collapsible to a slim handle. Read-only.
 */
import { type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { usePrescriptionImage } from './usePrescriptionImage';

interface Props {
  patientId: string;
  encounterId: string;
  patientName?: string | null;
  /** Docked/collapsed state (owned by the host so it persists). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export function PharmacyPrescriptionReference({
  patientId,
  encounterId,
  patientName,
  collapsed = false,
  onToggleCollapsed,
}: Props) {
  const brandTokens = useBrandTokens();
  // Only rasterize while expanded (collapsed shows just the handle).
  const { imgUrl, rendering, printDisabled, download, captureNode } = usePrescriptionImage(
    !collapsed,
    patientId,
    encounterId,
    patientName,
  );

  const container: CSSProperties = {
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 10001, // above the drawer (9999) + its backdrop; own strip only
    width: collapsed ? 44 : 'min(640px, 48vw)',
    transition: 'width 160ms ease',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-surface-2, #f8fafc)',
    borderRight: '1px solid var(--border)',
    boxShadow: '2px 0 10px rgba(0,0,0,0.05)',
    ...(brandTokens as CSSProperties),
  };

  return createPortal(
    <div style={container}>
      {collapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Show prescription reference"
          aria-label="Show prescription reference"
          style={{
            height: '100%',
            width: '100%',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--sp-3)',
            padding: 'var(--sp-3) 0',
            fontFamily: 'inherit',
          }}
        >
          <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
            <NavIcon name="chevron-right" />
          </span>
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Prescription
          </span>
        </button>
      ) : (
        <>
          {/* Header */}
          <div
            style={{
              padding: 'var(--sp-4) var(--sp-5)',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 'var(--sp-2)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text-muted)',
                }}
              >
                Prescription
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                Reference · read-only
              </div>
            </div>
            {onToggleCollapsed && (
              <button
                type="button"
                onClick={onToggleCollapsed}
                title="Collapse prescription reference"
                aria-label="Collapse prescription reference"
                style={{
                  flexShrink: 0,
                  width: 26,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <NavIcon name="chevron-left" />
              </button>
            )}
          </div>

          {/* Body — rendered prescription image */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 'var(--sp-4)' }}>
            {printDisabled ? (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                No prescription items recorded.
              </div>
            ) : imgUrl ? (
              <img
                src={imgUrl}
                alt="Prescription"
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}
              />
            ) : (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Preparing prescription…
              </div>
            )}
          </div>

          {/* Footer — download the same rendered document */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              padding: 'var(--sp-3) var(--sp-4)',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="outline"
              size="md"
              onClick={download}
              disabled={printDisabled || !imgUrl}
            >
              {rendering ? 'Preparing…' : 'Download'}
            </Button>
          </div>
        </>
      )}

      {/* Offscreen capture node (body-level → no transform interference). */}
      {captureNode}
    </div>,
    document.body,
  );
}
